import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/firebase";

import { resolveSubtopicBand } from "./resolveSubtopicBand";
import { MATH_SUBTOPICS } from "../../data/skillTaxonomy";
import { MATH_PILLARS } from "../../data/pillars";

import { estimatePillarScore } from "../analytics/estimatePillarScore";
import { rawToScaledScore } from "../analytics/rawToScaledScore";
import { saveAnalyticsResults } from "../analytics/saveAnalyticsResults";


const MATH_DIAGNOSTIC_PROPAGATION = {
  "2vle": ["1vle"],
  "lse": ["lf"],
  "nse": ["1vne", "nf"],
  "2vd": ["1vd"],
  "esc": ["is"],
};

/**
 * Submits a completed math diagnostic and assigns placement bands,
 * then recomputes and saves pillar + overall score estimates.
 */
export async function submitMathDiagnostic({ userUid, diagnosticAttempt }) {
  if (!userUid || !diagnosticAttempt) {
    throw new Error("submitMathDiagnostic: missing arguments");
  }

  const { responses, sequence } = diagnosticAttempt;

  /* ======================================================
     1. Resolve bands per subtopic → per skill
     ====================================================== */

  const analyticsSkills = {};
  const subtopicAnswers = {};

  sequence.forEach((qid) => {
    const match = qid.match(/^math_diag_([a-z0-9]+)_([123])_[ab]$/);
    if (!match) return;

    const [, subtopic, level] = match;

    if (!subtopicAnswers[subtopic]) {
      subtopicAnswers[subtopic] = [];
    }

    subtopicAnswers[subtopic].push({
      level,
      correct: Boolean(responses[qid]?.correct),
    });
  });

  const subtopicBandMap = {};

  Object.entries(subtopicAnswers).forEach(([subtopic, answers]) => {
    if (answers.length !== 3) return;
    subtopicBandMap[subtopic] = resolveSubtopicBand(answers);
  });

  /* ======================================================
     2. Propagate bands to implied subtopics
     ====================================================== */

  Object.entries(MATH_DIAGNOSTIC_PROPAGATION).forEach(
    ([source, targets]) => {
      const sourceBand = subtopicBandMap[source];
      if (!sourceBand) return;

      targets.forEach((target) => {
        if (!subtopicBandMap[target]) {
          subtopicBandMap[target] = sourceBand;
        }
      });
    }
  );

  /* ======================================================
     3. Assign bands to ALL math skills
     ====================================================== */

  Object.entries(subtopicBandMap).forEach(([subtopic, band]) => {
    const skills = MATH_SUBTOPICS[subtopic]?.skills;

    if (!skills) {
      console.warn(`Unknown subtopic code: ${subtopic}`);
      return;
    }

    skills.forEach((skillCode) => {
      analyticsSkills[skillCode] = {
        band,
        attempted: 0,
        accuracy: null,
        bandProgress: 0,
        canPromote: false,
        lastCompletedAt: serverTimestamp(),
      };
    });
  });

  /* ======================================================
     4. Write analytics.skills (canonical state)
     ====================================================== */

  const userRef = doc(db, "users", userUid);

  await updateDoc(userRef, {
    "analytics.math.skills": analyticsSkills,
    "analytics.math.updatedAt": serverTimestamp(),
  });

  /* ======================================================
     5. Recompute pillar estimates (Math)
     ====================================================== */

  const mathSkillBands = Object.fromEntries(
    Object.entries(analyticsSkills).map(([k, v]) => [k, v.band])
  );

  const mathPillars = MATH_PILLARS.map((pillar) => {
    const rows = estimatePillarScore({
      pillar,
      skillBandMap: mathSkillBands,
      totalQuestions: 54,
    });

    const correctLow = rows.reduce((s, r) => s + (r.correctLow || 0), 0);
    const correctEst = rows.reduce((s, r) => s + (r.correctEst || 0), 0);
    const correctHigh = rows.reduce((s, r) => s + (r.correctHigh || 0), 0);

    return {
      id: pillar.id,
      low: rawToScaledScore(correctLow, "math", "lower"),
      est: rawToScaledScore(correctEst, "math", "middle"),
      high: rawToScaledScore(correctHigh, "math", "upper"),
    };
  });

  /* ======================================================
     6. Aggregate overall Math using pillarWeight
     ====================================================== */

  const mathOverall = MATH_PILLARS.reduce(
    (acc, pillar) => {
      const p = mathPillars.find((x) => x.id === pillar.id);
      if (!p) return acc;

      acc.low += p.low * pillar.pillarWeight;
      acc.est += p.est * pillar.pillarWeight;
      acc.high += p.high * pillar.pillarWeight;

      return acc;
    },
    { low: 0, est: 0, high: 0 }
  );

  // Round to nearest 10 (SAT scores)
  Object.keys(mathOverall).forEach((k) => {
    mathOverall[k] = Math.round(mathOverall[k] / 10) * 10;
  });

  /* ======================================================
     7. Persist results
     ====================================================== */

  await saveAnalyticsResults({
    uid: userUid,

    // overwrite current pillar state
    pillarEstimates: {
      math: Object.fromEntries(
        mathPillars.map((p) => [p.id, p])
      ),
    },

    // append / override daily overall history
    overallEstimate: {
      math: mathOverall,
    },

    source: "diagnostic",
  });
}
